<template>
    <Head :title="`Template: ${template.title}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <div class="flex items-center space-x-3">
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ template.title }}</h1>
                    <span :class="getStatusClass(template.status)">
                        {{ getStatusLabel(template.status) }}
                    </span>
                </div>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {{ template.template_code }} • {{ template.type?.name }}
                </p>
            </div>
            <div class="flex items-center space-x-3">
                <Link
                    :href="route('checklists.templates.index')"
                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                    Back to Templates
                </Link>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Main Content -->
            <div class="lg:col-span-2 space-y-6">
                <!-- Template Info -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Template Information</h2>
                    <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Template Code</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ template.template_code }}</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Type</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ template.type?.name || 'N/A' }}</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Sections</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ template.sections?.length || 0 }}</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Fields</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ countFields() }}</dd>
                        </div>
                        <div v-if="template.scoring_enabled">
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Passing Score</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ template.passing_score }}%</dd>
                        </div>
                        <div v-if="template.scoring_enabled">
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Scoring Method</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white capitalize">{{ template.scoring_method }}</dd>
                        </div>
                    </dl>

                    <div v-if="template.description" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Description</dt>
                        <dd class="text-sm text-gray-900 dark:text-white">{{ template.description }}</dd>
                    </div>
                </div>

                <!-- Sections & Fields Preview -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Template Structure</h2>

                    <div v-if="template.sections && template.sections.length > 0" class="space-y-6">
                        <div v-for="(section, sIndex) in template.sections" :key="section.id" class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div class="flex items-center justify-between mb-3">
                                <h3 class="text-md font-medium text-gray-900 dark:text-white">
                                    {{ sIndex + 1 }}. {{ section.title }}
                                </h3>
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ section.fields?.length || 0 }} fields
                                </span>
                            </div>
                            <p v-if="section.description" class="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {{ section.description }}
                            </p>

                            <div v-if="section.fields && section.fields.length > 0" class="space-y-3">
                                <div
                                    v-for="(field, fIndex) in section.fields"
                                    :key="field.id"
                                    class="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600"
                                >
                                    <div class="flex-1">
                                        <div class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ sIndex + 1 }}.{{ fIndex + 1 }} {{ field.label }}
                                            <span v-if="field.is_required" class="text-red-500 ml-1">*</span>
                                        </div>
                                        <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {{ getFieldTypeLabel(field.field_type) }}
                                            <span v-if="field.points" class="ml-2 text-blue-600 dark:text-blue-400">
                                                • {{ field.points }} pts
                                            </span>
                                        </div>
                                        <p v-if="field.help_text" class="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                                            {{ field.help_text }}
                                        </p>

                                        <!-- Field Supplements -->
                                        <div v-if="getFieldSupplements(field).length > 0" class="flex flex-wrap gap-2 mt-2">
                                            <span
                                                v-for="supplement in getFieldSupplements(field)"
                                                :key="supplement.label"
                                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                                            >
                                                {{ supplement.icon }} {{ supplement.label }}
                                            </span>
                                        </div>

                                        <!-- Dependency Information -->
                                        <div v-if="getDependencyInfo(field)" class="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800">
                                            <div class="flex items-start">
                                                <svg class="w-4 h-4 text-purple-600 dark:text-purple-400 mr-1 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />
                                                </svg>
                                                <div class="text-xs text-purple-800 dark:text-purple-300">
                                                    <span class="font-medium">Dependency:</span>
                                                    {{ getActionLabel(getDependencyInfo(field).action) }} when
                                                    "<span class="font-medium">{{ getDependencyInfo(field).parentLabel }}</span>"
                                                    {{ getConditionLabel(getDependencyInfo(field).condition) }}
                                                    "<span class="font-medium">{{ getDependencyInfo(field).value }}</span>"
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Desired Answer -->
                                        <div v-if="getDesiredAnswerInfo(field)" class="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                            <div class="flex items-start">
                                                <svg class="w-4 h-4 text-green-600 dark:text-green-400 mr-1 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                                </svg>
                                                <div class="text-xs text-green-800 dark:text-green-300">
                                                    <span class="font-medium">Desired Answer:</span>
                                                    <span v-if="getDesiredAnswerInfo(field).type === 'any_value'"> Any value</span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'not_empty'"> Not empty</span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'specific_value'">
                                                        "{{ getDesiredAnswerInfo(field).value }}"
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'specific_values'">
                                                        {{ formatDesiredAnswerValues(getDesiredAnswerInfo(field).values) }}
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'any_of'">
                                                        {{ formatDesiredAnswerValues(getDesiredAnswerInfo(field).any_of) }}
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'range'">
                                                        Between {{ getDesiredAnswerInfo(field).min }} and {{ getDesiredAnswerInfo(field).max }}
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'minimum'">
                                                        At least {{ getDesiredAnswerInfo(field).value }}
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'maximum'">
                                                        At most {{ getDesiredAnswerInfo(field).value }}
                                                    </span>
                                                    <span v-else-if="getDesiredAnswerInfo(field).type === 'contains'">
                                                        Contains "{{ getDesiredAnswerInfo(field).value }}"
                                                    </span>
                                                    <span v-else>
                                                        {{ formatDesiredAnswerFallback(getDesiredAnswerInfo(field)) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                No fields in this section
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-12 text-gray-500 dark:text-gray-400">
                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p class="mt-2">No sections defined yet</p>
                    </div>
                </div>
            </div>

            <!-- Sidebar -->
            <div class="space-y-6">
                <!-- Actions -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Actions</h3>
                    <div class="space-y-3">
                        <!-- Preview -->
                        <button
                            @click="showPreviewModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Preview Template
                        </button>

                        <!-- Publish -->
                        <button
                            v-if="template.status === 'draft'"
                            @click="showPublishModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Publish Template
                        </button>

                        <!-- Edit Info -->
                        <button
                            @click="showEditModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit Template Info
                        </button>

                        <!-- Edit Fields -->
                        <Link
                            v-if="template.status === 'draft'"
                            :href="route('checklists.templates.edit', template.id)"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Template Fields
                        </Link>

                        <!-- Duplicate -->
                        <button
                            @click="showDuplicateModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Duplicate
                        </button>

                        <!-- Delete -->
                        <button
                            @click="showDeleteModal = true"
                            class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete Template
                        </button>
                    </div>
                </div>

                <!-- Stats -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Statistics</h3>
                    <dl class="space-y-3">
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Times Used</dt>
                            <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{{ template.checklists_count || 0 }}</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(template.created_at) }}</dd>
                        </div>
                        <div v-if="template.published_at">
                            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Published</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(template.published_at) }}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        <!-- Edit Template Info Modal -->
        <div v-if="showEditModal" class="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Center alignment helper -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" @click="showEditModal = false"></div>

                <!-- Modal panel -->
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <form @submit.prevent="updateTemplateInfo">
                        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4" id="modal-title">
                                        Edit Template Information
                                    </h3>
                                    <div class="space-y-4">
                                        <!-- Template Title -->
                                        <div>
                                            <label for="template-title" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Template Title <span class="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="template-title"
                                                v-model="editForm.title"
                                                type="text"
                                                required
                                                maxlength="200"
                                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <!-- Template Description -->
                                        <div>
                                            <label for="template-description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                id="template-description"
                                                v-model="editForm.description"
                                                rows="4"
                                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                type="submit"
                                :disabled="editForm.processing"
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {{ editForm.processing ? 'Saving...' : 'Save Changes' }}
                            </button>
                            <button
                                type="button"
                                @click="showEditModal = false"
                                :disabled="editForm.processing"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Preview Template Modal -->
        <div v-if="showPreviewModal" class="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="preview-modal-title" role="dialog" aria-modal="true">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Center alignment helper -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" @click="showPreviewModal = false"></div>

                <!-- Modal panel -->
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div class="bg-white dark:bg-gray-800">
                        <!-- Modal Header -->
                        <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                            <div class="flex items-center justify-between">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="preview-modal-title">
                                    Template Preview
                                </h3>
                                <button
                                    type="button"
                                    @click="showPreviewModal = false"
                                    class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                >
                                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Modal Body with Preview -->
                        <div class="px-6 py-6 max-h-[70vh] overflow-y-auto">
                            <BuilderCanvas
                                :sections="template.sections || []"
                                :preview-mode="true"
                                :selected-field-id="null"
                            />
                        </div>

                        <!-- Modal Footer -->
                        <div class="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                            <button
                                type="button"
                                @click="showPreviewModal = false"
                                class="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="delete-modal-title" role="dialog" aria-modal="true">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Center alignment helper -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>

                <!-- Modal panel -->
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="delete-modal-title">
                                    Delete Template
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">
                                        Are you sure you want to delete this template? This action cannot be undone. All sections and fields will be permanently removed.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            @click="deleteTemplate"
                            :disabled="deleteForm.processing"
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ deleteForm.processing ? 'Deleting...' : 'Delete' }}
                        </button>
                        <button
                            type="button"
                            @click="showDeleteModal = false"
                            :disabled="deleteForm.processing"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Publish Confirmation Modal -->
        <div v-if="showPublishModal" class="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="publish-modal-title" role="dialog" aria-modal="true">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Center alignment helper -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" @click="showPublishModal = false"></div>

                <!-- Modal panel -->
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="publish-modal-title">
                                    Publish Template
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">
                                        Are you sure you want to publish this template? Once published, the template fields cannot be edited. You can only modify the template title and description.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            @click="publishTemplate"
                            :disabled="publishForm.processing"
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ publishForm.processing ? 'Publishing...' : 'Publish' }}
                        </button>
                        <button
                            type="button"
                            @click="showPublishModal = false"
                            :disabled="publishForm.processing"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Duplicate Confirmation Modal -->
        <div v-if="showDuplicateModal" class="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="duplicate-modal-title" role="dialog" aria-modal="true">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Center alignment helper -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" @click="showDuplicateModal = false"></div>

                <!-- Modal panel -->
                <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="duplicate-modal-title">
                                    Duplicate Template
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">
                                        Are you sure you want to duplicate this template? A new draft copy will be created with all sections and fields. The new template will be named "{{ template.title }} (Copy)".
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            @click="duplicateTemplate"
                            :disabled="duplicateForm.processing"
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ duplicateForm.processing ? 'Duplicating...' : 'Duplicate' }}
                        </button>
                        <button
                            type="button"
                            @click="showDuplicateModal = false"
                            :disabled="duplicateForm.processing"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import BuilderCanvas from '@/Components/Checklists/Builder/BuilderCanvas.vue';

const props = defineProps({
    template: Object,
    companyOwner: {
        type: Object,
        default: null,
    },
});

const page = usePage();
const { formatDateTime: formatDateTimeInTZ } = useTimezone();

// Modal states
const showEditModal = ref(false);
const showPreviewModal = ref(false);
const showDeleteModal = ref(false);
const showPublishModal = ref(false);
const showDuplicateModal = ref(false);

// Edit form
const editForm = reactive({
    title: props.template.title,
    description: props.template.description || '',
    processing: false,
});

// Delete form
const deleteForm = reactive({
    processing: false,
});

// Publish form
const publishForm = reactive({
    processing: false,
});

// Duplicate form
const duplicateForm = reactive({
    processing: false,
});

// Update template info
const updateTemplateInfo = () => {
    editForm.processing = true;

    router.put(route('checklists.templates.update', props.template.id), {
        title: editForm.title,
        description: editForm.description,
        _company_id: page.props.company_id,
    }, {
        onSuccess: () => {
            showEditModal.value = false;
            editForm.processing = false;
        },
        onError: () => {
            editForm.processing = false;
        },
    });
};

// Delete template
const deleteTemplate = () => {
    deleteForm.processing = true;

    router.delete(route('checklists.templates.destroy', props.template.id), {
        data: {
            _company_id: page.props.company_id,
        },
        onSuccess: () => {
            showDeleteModal.value = false;
            deleteForm.processing = false;
        },
        onError: () => {
            deleteForm.processing = false;
        },
    });
};

const fieldTypes = {
    yes_no: 'Yes/No',
    pass_fail_na: 'Pass/Fail/N/A',
    multiple_choice: 'Multiple Choice',
    multiple_selection: 'Checkboxes',
    dropdown_select: 'Dropdown',
    text_input: 'Short Text',
    textarea: 'Long Text',
    number_input: 'Number',
    decimal_input: 'Decimal',
    date_picker: 'Date',
    time_picker: 'Time',
    datetime_picker: 'Date & Time',
    rating_scale: 'Rating',
    signature: 'Signature',
    photo_capture: 'Photo',
    file_upload: 'File Upload',
};

const getStatusClass = (status) => {
    const classes = {
        draft: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        published: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        archived: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || classes.draft;
};

const getStatusLabel = (status) => {
    const labels = {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived',
    };
    return labels[status] || status;
};

const getFieldTypeLabel = (type) => {
    return fieldTypes[type] || type;
};

const countFields = () => {
    if (!props.template.sections) return 0;
    return props.template.sections.reduce((total, section) => {
        return total + (section.fields?.length || 0);
    }, 0);
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date);
};

const publishTemplate = () => {
    publishForm.processing = true;

    router.post(route('checklists.templates.publish', props.template.id), {
        _company_id: page.props.company_id,
    }, {
        onSuccess: () => {
            showPublishModal.value = false;
            publishForm.processing = false;
        },
        onError: () => {
            publishForm.processing = false;
        },
    });
};

const duplicateTemplate = () => {
    duplicateForm.processing = true;

    router.post(route('checklists.templates.duplicate', props.template.id), {
        _company_id: page.props.company_id,
    }, {
        onSuccess: () => {
            showDuplicateModal.value = false;
            duplicateForm.processing = false;
        },
        onError: () => {
            duplicateForm.processing = false;
        },
    });
};

const archiveTemplate = () => {
    if (confirm('Are you sure you want to archive this template?')) {
        router.post(route('checklists.templates.deactivate', props.template.id), {
            _company_id: page.props.company_id,
        });
    }
};

const getFieldSupplements = (field) => {
    const supplements = [];
    if (field.allow_notes) supplements.push({ icon: '📝', label: field.require_notes ? 'Notes (Required)' : 'Notes' });
    if (field.allow_photos) supplements.push({ icon: '📷', label: field.require_photos ? 'Photos (Required)' : 'Photos' });
    if (field.allow_attachments) supplements.push({ icon: '📎', label: field.require_attachments ? 'Attachments (Required)' : 'Attachments' });
    return supplements;
};

const getParentFieldLabel = (parentFieldId) => {
    if (!parentFieldId || !props.template.sections) return null;

    for (const section of props.template.sections) {
        const parentField = section.fields?.find(f => f.id === parentFieldId);
        if (parentField) return parentField.label;
    }
    return 'Unknown Field';
};

const getDependencyInfo = (field) => {
    if (!field.parent_field_id || !field.dependency_rule) return null;

    const parentLabel = getParentFieldLabel(field.parent_field_id);
    let rule = field.dependency_rule;

    // Parse JSON if string
    if (typeof rule === 'string') {
        try {
            rule = JSON.parse(rule);
        } catch (e) {
            return null;
        }
    }

    return {
        parentLabel,
        condition: rule.condition || 'equals',
        value: rule.value,
        action: rule.action || 'show_and_require'
    };
};

const getDesiredAnswerInfo = (field) => {
    if (!field.desired_answer) return null;

    let answer = field.desired_answer;

    // Parse JSON if string
    if (typeof answer === 'string') {
        try {
            answer = JSON.parse(answer);
        } catch (e) {
            return { type: 'specific_value', value: answer };
        }
    }

    return answer;
};

const getActionLabel = (action) => {
    const labels = {
        'show_and_require': 'Show (required)',
        'show_optional': 'Show (optional)',
        'hide': 'Hide'
    };
    return labels[action] || 'Show';
};

const getConditionLabel = (condition) => {
    const labels = {
        'equals': 'equals',
        'not_equals': 'does not equal',
        'contains': 'contains',
        'greater_than': 'is greater than',
        'less_than': 'is less than'
    };
    return labels[condition] || condition;
};

const formatDesiredAnswerValues = (values) => {
    if (!values || !Array.isArray(values)) return '';
    if (values.length === 0) return 'No values specified';
    if (values.length === 1) return `"${values[0]}"`;
    if (values.length === 2) return `"${values[0]}" or "${values[1]}"`;

    const lastValue = values[values.length - 1];
    const otherValues = values.slice(0, -1).map(v => `"${v}"`).join(', ');
    return `${otherValues}, or "${lastValue}"`;
};

const formatDesiredAnswerFallback = (answerInfo) => {
    if (!answerInfo) return '';

    // If it has a value property, display it
    if (answerInfo.value !== undefined && answerInfo.value !== null) {
        return `"${answerInfo.value}"`;
    }

    // If it has values array, display them
    if (answerInfo.values && Array.isArray(answerInfo.values)) {
        return formatDesiredAnswerValues(answerInfo.values);
    }

    // Try to display the object as JSON (for debugging)
    try {
        const str = JSON.stringify(answerInfo);
        if (str && str !== '{}') {
            return str;
        }
    } catch (e) {
        // Ignore
    }

    return 'Value specified';
};
</script>
